'use client';
import dynamic from 'next/dynamic';

import { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import { TabsID } from '@shared/api/types/magicWall';
import { ADS_CONSTANTS, useUserAdSettings } from '@shared/common/components/NavLayout/AdBlocks';
import { useAuth } from '@shared/common/providers/AuthProvider';

import { useMagicWallInfinityQuery } from '@query/magicWall';

import type { Tabs} from './constants/tabs';
import { tabs } from './constants/tabs';
import { useCategoryFilter, useSortFilter } from './hooks';

const StoreMainInlineTopBannerTextButton = dynamic(
  () => import('@shared/common/components/NavLayout/AdBlocks/StoreMainInlineTopBannerTextButton'),
  {
    ssr: false,
  },
);

const MagicOrderWallLayout = () => {
  const { isAuth } = useAuth();
  const [selectedTabId, setSelectedTabId] = useState(tabs[0].id);
  const [sort, setSort] = useSortFilter();
  const { showStoreMainInlineTopBannerTextButton } = useUserAdSettings();

  const [categoryFilter, setCategoryFilter] = useCategoryFilter();
  const activeTab = tabs.find((tab) => tab.id === selectedTabId) as Tabs;

  const query = useMagicWallInfinityQuery({
    category: Number(categoryFilter),
    sort,
    status: selectedTabId === TabsID.Tasks ? 'not_completed' : 'completed',
  });

  const list = useMemo(
    () => query.data?.pages.flatMap((page) => page.data ?? []) || [],
    [query.data],
  );

  useEffect(() => {
    if (isAuth && !list.length) {
      query.refetch();
    }
  }, [isAuth, list.length, query]);

  const filteredTabs = !isAuth ? tabs.filter((tab) => tab.id === selectedTabId) : tabs;

  const { Component } = activeTab || {};
  const hasComponent = Boolean(Component);

  const handleClickTab = (id: keyof typeof TabsID) => () => setSelectedTabId(id);

  return (
    <main
      className={classNames('v2-container', {
        'store-container-pt': !showStoreMainInlineTopBannerTextButton,
      })}
    >
      <StoreMainInlineTopBannerTextButton
        dataKey={ADS_CONSTANTS.storeMainInlineTopBannerTextButtonDataKey}
      />
      <div role="tablist" className="flex shadow-[inset_0_-1px] shadow-neutral-200">
        {filteredTabs.map((tab) => {
          const isSelected = tab.id === activeTab.id;

          return (
            <button
              className={classNames('p-4 flex items-center justify-center', {
                'text-primary-700 shadow-[inset_0_-4px] shadow-primary-700': isSelected,
                'text-neutral-500': !isSelected,
              })}
              data-qa="magic-board-tab"
              key={tab.id}
              onClick={handleClickTab(tab.id)}
              type="button"
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {hasComponent && (
        <Component
          query={query}
          list={list}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          sort={sort}
          setSort={setSort}
        />
      )}
    </main>
  );
};

export default MagicOrderWallLayout;
